/**
 * Boutique TEXTIPRINT — Clients (Sprint 1 MVP).
 * Brief SIM-PLATFORM-BOUTIQUE-2026-001 §2.5 B4.
 *
 * MVP : KPIs + filtres + table + toggle actif. Création/édition complète
 * + onglets adresses/fichiers logos arrivent en Sprint 1.5.
 */
import { useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, Users, Building2, ShoppingBag, Wallet } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useClientsBoutique, useUpdateClientBoutique } from '@/hooks/useBoutique';
import {
  TYPE_CLIENT_BOUTIQUE_LABELS,
  type ClientBoutique, type TypeClientBoutique,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

export const BoutiqueClientsPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [typeFilter, setTypeFilter] = useState<TypeClientBoutique | ''>('');
  const [actifFilter, setActifFilter] = useState<'all' | 'on' | 'off'>('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const queryParams = useMemo(() => ({
    limit: 500,
    type:    typeFilter || undefined,
    actif:   actifFilter === 'all' ? undefined : actifFilter === 'on',
    search:  search.trim() || undefined,
  }), [typeFilter, actifFilter, search]);

  const { data, isLoading, refetch } = useClientsBoutique(queryParams);
  const clients = data?.data ?? [];
  const update = useUpdateClientBoutique();

  // KPIs
  const totalB2C = clients.filter((c) => c.type === 'b2c_particulier').length;
  const totalB2B = clients.filter((c) => c.type === 'b2b_pro').length;
  const totalImpr = clients.filter((c) => c.type === 'imprimeur_partenaire').length;
  const totalLtvCents = clients.reduce((s, c) => s + Number(BigInt(c.totalDepenseCents ?? '0')), 0);

  const toggleActif = async (c: ClientBoutique) => {
    setError(null);
    try { await update.mutateAsync({ id: c.id, payload: { actif: !c.actif } }); }
    catch (err) { setError(extractError(err).message); }
  };

  const typeBadgeClass: Record<TypeClientBoutique, string> = {
    b2c_particulier:      'ds-bd ds-bd-g',
    b2b_pro:              'ds-bd ds-bd-info',
    imprimeur_partenaire: 'ds-bd ds-bd-warn',
  };

  return (
    <>
      <Topbar
        title="Clients Boutique"
        subtitle={filiale ? `${clients.length} client(s) · ${filiale.fullName}` : 'Clients boutique'}
        actions={
          <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </button>
        }
      />
      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1">
            <div className="ds-kr-ico"><Users size={18} /></div>
            <div className="ds-kr-val">{clients.length}</div>
            <div className="ds-kr-lbl">Total clients</div>
          </div>
          <div className="ds-kr kr-c2">
            <div className="ds-kr-ico"><ShoppingBag size={18} /></div>
            <div className="ds-kr-val">{totalB2C}</div>
            <div className="ds-kr-lbl">B2C Particuliers</div>
          </div>
          <div className="ds-kr kr-c3">
            <div className="ds-kr-ico"><Building2 size={18} /></div>
            <div className="ds-kr-val">{totalB2B}</div>
            <div className="ds-kr-lbl">B2B Pro</div>
          </div>
          <div className="ds-kr kr-c4">
            <div className="ds-kr-ico">🤝</div>
            <div className="ds-kr-val">{totalImpr}</div>
            <div className="ds-kr-lbl">Imprimeurs partenaires</div>
          </div>
          <div className="ds-kr kr-c5">
            <div className="ds-kr-ico"><Wallet size={18} /></div>
            <div className="ds-kr-val">{formatNumber(totalLtvCents / 100)}</div>
            <div className="ds-kr-lbl">LTV cumulé (FCFA)</div>
          </div>
        </div>

        <div className="ds-filters" style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Rechercher email / nom / téléphone…"
            className="ds-fi"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 280 }}
          />
          <select
            className="ds-fi"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeClientBoutique | '')}
            style={{ width: 'auto' }}
          >
            <option value="">Tous types</option>
            {(Object.keys(TYPE_CLIENT_BOUTIQUE_LABELS) as TypeClientBoutique[]).map((t) => (
              <option key={t} value={t}>{TYPE_CLIENT_BOUTIQUE_LABELS[t]}</option>
            ))}
          </select>
          <select
            className="ds-fi"
            value={actifFilter}
            onChange={(e) => setActifFilter(e.target.value as 'all' | 'on' | 'off')}
            style={{ width: 'auto' }}
          >
            <option value="all">Tous statuts</option>
            <option value="on">Actifs uniquement</option>
            <option value="off">Suspendus uniquement</option>
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Clients Boutique</div>
              <div className="ds-table-sub">
                {clients.length} client{clients.length > 1 ? 's' : ''}
                {' · '}MVP Sprint 1 — édition complète bientôt
              </div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nom complet</th>
                <th>Raison sociale</th>
                <th style={{ width: 150 }}>Type</th>
                <th style={{ width: 120 }}>Phone</th>
                <th style={{ width: 130 }}>LTV (FCFA)</th>
                <th style={{ width: 90 }}>Remise</th>
                <th style={{ width: 100 }}>Statut</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--tx-3)' }}>Aucun client (les clients seront créés en Sprint 3 vitrine publique ou via API)</td></tr>
              ) : clients.map((c) => (
                <tr key={c.id}>
                  <td><code style={{ fontSize: 11, color: 'var(--tx-2)' }}>{c.email}</code></td>
                  <td style={{ fontWeight: 500 }}>{c.nomComplet}</td>
                  <td>{c.raisonSociale ?? <span style={{ color: 'var(--tx-3)' }}>—</span>}</td>
                  <td>
                    <span className={typeBadgeClass[c.type]}>
                      {TYPE_CLIENT_BOUTIQUE_LABELS[c.type]}
                    </span>
                  </td>
                  <td>{c.phone ?? '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMontant(c.totalDepenseCents ?? '0')}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {Number(c.remisePermanentePct) > 0 ? `${c.remisePermanentePct}%` : '—'}
                  </td>
                  <td>
                    <button
                      className={`ds-btn ds-btn-sm ${c.actif ? 'ds-btn-s' : 'ds-btn-d'}`}
                      onClick={() => void toggleActif(c)}
                      disabled={update.isPending}
                    >
                      {c.actif ? 'Actif' : 'Suspendu'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
